import {ContentEditableTextEditor} from '@frontapp/editor-html';
import React, {FC, RefObject, useRef} from 'react';

import {containsFocus, isInputLike, isNode} from '../../../../../libs/shared/helpers/src/browser/domHelpers';
import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {useToggle} from '../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {
  FrontFocusEvent,
  FrontKeyboardEvent,
  FrontMouseEvent,
  Handler,
  HandlerOf,
} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {UnreachableError} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {findMemoizedActiveToken} from '../../../../../srcCommon/inboxes/search/searchQueryHelpers';
import {SuggestionSelectHandler} from '../../../../../srcCommon/inboxes/search/searchSuggestionsHelpers';
import {InboxesSearchLocationsEnum} from '../../../../models/url/models/inboxesUrlModel';
import {InboxesParameters} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {ServiceSubscription} from '../../../common/editor/serviceSubscription';
import {SearchBar} from '../../../common/searchBar/searchBar';
import {CellsHeaderFiltersProps, OnFilterSelectHandler} from '../../cells/header/cellsHeaderFilters';
import {InboxesSearchFilters} from './filters/inboxesSearchFilters';
import {InboxesSearchQueryBuilder} from './queryBuilder/inboxesSearchQueryBuilder';
import {InboxesSearchSuggestionsDropdown} from './suggestions/inboxesSearchSuggestionsDropdown';

/*
 * Props.
 */

interface InboxesSearchBarContentProps {
  isDisabled: boolean;
  isInSearch: boolean;
  onKeyDown: HandlerOf<FrontKeyboardEvent>;
  onBlur: Handler;

  // Query hash.
  queryHash?: string;
  maybeUpdateSearchSessionTimestamp: Handler;

  // Editor.
  editor: ContentEditableTextEditor;
  editorHasFocus: boolean;
  onExitClick: Handler;

  // Filters.
  searchQuery?: string;
  searchFilters: CellsHeaderFiltersProps<InboxesSearchLocationsEnum>;
  temporarySearchFilter?: InboxesSearchLocationsEnum;
  selectedFilter: InboxesSearchLocationsEnum;
  onFilterSelect?: OnFilterSelectHandler<InboxesSearchLocationsEnum>;
  onLocalFilterSelect: HandlerOf<InboxesSearchLocationsEnum>;

  // Search suggestions.
  namespace?: string;
  parameters: InboxesParameters;
  onSuggestionSelect: SuggestionSelectHandler;

  // Query builder.
  isQueryBuilderDropdownOpen: boolean;
  toggleIsQueryBuilderDropdownOpen: Handler;
  onQueryBuilderSearch: HandlerOf<string>;
}

/*
 * Component.
 */

export const InboxesSearchBarContent: FC<InboxesSearchBarContentProps> = (props) => {
  const {
    isDisabled,
    isInSearch,
    onKeyDown,
    onBlur,
    queryHash,
    maybeUpdateSearchSessionTimestamp,
    editor,
    editorHasFocus,
    onExitClick,
    searchQuery,
    searchFilters,
    temporarySearchFilter,
    selectedFilter,
    onFilterSelect,
    onLocalFilterSelect,
    namespace,
    parameters,
    onSuggestionSelect,
    isQueryBuilderDropdownOpen,
    toggleIsQueryBuilderDropdownOpen,
    onQueryBuilderSearch,
  } = props;

  const searchBarWrapperRef = useRef<HTMLDivElement>(null);
  const queryBuilderDropdownRef = useRef<HTMLDivElement>(null);

  const editorHadFocusOnDropdownMouseDown = useRef(false);
  const shouldRefocusEditor = editorHadFocusOnDropdownMouseDown.current;

  const [isFiltersDropdownOpen, toggleIsFiltersDropdownOpen] = useToggle();

  const toggleFiltersDropdown = () => {
    if (isFiltersDropdownOpen && shouldRefocusEditor) {
      // Focus the editor.
      focusEditor();

      // Reset `editorHadFocusOnDropdownMouseDown`.
      editorHadFocusOnDropdownMouseDown.current = false;
    }

    // Toggle `isFiltersDropdownOpen`.
    toggleIsFiltersDropdownOpen();
  };

  const onSearchFilterSelect = (filter: InboxesSearchLocationsEnum) => {
    // If searchQuery length is zero, it means we're not searching.
    // We need to update the local filter (aka the overlay one).
    if (searchQuery?.length === 0 || shouldRefocusEditor) {
      onLocalFilterSelect(filter);
      return;
    }

    // Select the filter.
    if (onFilterSelect) {
      onFilterSelect(filter, {shouldPersistSelectedFilter: true});
    }
  };

  const toggleQueryBuilderDropdown = (skipRefocus: boolean) => {
    if (isQueryBuilderDropdownOpen && shouldRefocusEditor) {
      if (!skipRefocus) {
        // Focus the editor.
        focusEditor();
      }

      // Reset `editorHadFocusOnDropdownMouseDown`.
      editorHadFocusOnDropdownMouseDown.current = false;
    }

    if (!isQueryBuilderDropdownOpen) {
      // Maybe update search session timestamp - basically if search bar was not focused.
      maybeUpdateSearchSessionTimestamp();
    }

    // Toggle `isFiltersDropdownOpen`.
    toggleIsQueryBuilderDropdownOpen();
  };

  // Search bar has focus if focus is either on the filter dropdown or the editor.
  const searchBarHasFocus = Boolean(
    searchBarWrapperRef.current && containsFocus(searchBarWrapperRef.current),
  );
  const shouldDisplayFocus = isInSearch ? editorHasFocus || shouldRefocusEditor : searchBarHasFocus;
  const selectedSearchFilter = temporarySearchFilter || searchFilters.selectedFilter;

  /*
   * Input methods.
   */

  const focusEditor = () => {
    // Maybe update search session timestamp.
    maybeUpdateSearchSessionTimestamp();

    editor.services.focus.focus();
  };

  /*
   * Event handlers.
   */

  const onSearchBarBlur = (event: FrontFocusEvent<HTMLDivElement>) => {
    // We need to check if the relatedTarget is inside the search bar to avoid erasing a temporary query.
    const isRelatedTargetInsideSearchBar = isRelatedTargetInsideRef(event, searchBarWrapperRef);

    // If we're still inside the search bar, return early.
    if (isRelatedTargetInsideSearchBar) {
      return;
    }

    // Make sure to close filters dropdown if it was open.
    if (isFiltersDropdownOpen) {
      toggleFiltersDropdown();
    }

    // Maybe close query builder dropdown if it was open - except if the user clicked inside it.
    if (isQueryBuilderDropdownOpen) {
      const isRelatedTargetInsideQueryBuilderDropdown = isRelatedTargetInsideRef(
        event,
        queryBuilderDropdownRef,
      );

      if (isRelatedTargetInsideQueryBuilderDropdown) {
        return;
      }

      toggleQueryBuilderDropdown(false);
    }

    onBlur();
  };

  const onFiltersButtonMouseDown: HandlerOf<FrontMouseEvent> = (event) => {
    // If dropdown was not opened, and editor had focus, we should refocus it after closing the filters dropdown.
    if (!isFiltersDropdownOpen && editorHasFocus) {
      editorHadFocusOnDropdownMouseDown.current = true;
    }
  };

  const onQueryBuilderButtonMouseDown: HandlerOf<FrontMouseEvent> = (event) => {
    // If dropdown was not opened, and editor had focus, we should refocus it after closing the query builder dropdown.
    if (!isQueryBuilderDropdownOpen && editorHasFocus) {
      editorHadFocusOnDropdownMouseDown.current = true;
    }

    event.preventDefault();
  };

  // If the currently selected element is the input, cancel.
  const onEditorMouseDown: HandlerOf<FrontMouseEvent> = (event) => {
    if (!searchBarWrapperRef || isInputLike(event.target)) {
      return;
    }

    event.preventDefault();
  };

  const onExitMouseDown: HandlerOf<FrontMouseEvent> = (event) => {
    event.preventDefault();
  };

  // On a click on the box, focus the input.
  const onBoxClick = () => focusEditor();

  /*
   * Render.
   */

  const renderFilters = () => (
    <InboxesSearchFilters
      isOpen={isFiltersDropdownOpen}
      onToggleMouseDown={onFiltersButtonMouseDown}
      onToggleClick={toggleFiltersDropdown}
      isDisabled={isDisabled}
      hasFocus={shouldDisplayFocus}
      searchFilters={searchFilters}
      selectedFilter={selectedFilter}
      onSearchFilterSelect={onSearchFilterSelect}
    />
  );

  const renderPlaceholder = (hasFocus: boolean) => {
    if (hasFocus) {
      return <Trans id="search-advanced-placeholder">Type + for advanced search</Trans>;
    }

    switch (selectedSearchFilter) {
      case InboxesSearchLocationsEnum.LOCAL:
        return <Trans id="inboxes-search-placeholder">Search {searchFilters.localSearchFilterName}</Trans>;

      case InboxesSearchLocationsEnum.PINNED_IN_WORKSPACE:
        return <Trans id="inboxes-search-placeholder-sidebar">Search in my sidebar</Trans>;

      case InboxesSearchLocationsEnum.GLOBAL:
        return <Trans id="inboxes-search-placeholder-all">Search all inboxes</Trans>;

      default:
        // This should not happen.
        throw new UnreachableError(selectedSearchFilter);
    }
  };

  const renderLeftContent = (isEmpty: boolean) => {
    const shouldDisplayFiltersButton = searchBarHasFocus;

    if (!shouldDisplayFiltersButton) {
      return null;
    }

    return renderFilters();
  };

  const renderQueryBuilder = () => (
    <ServiceSubscription source={editor.services.text}>
      {({text}) => (
        <InboxesSearchQueryBuilder
          searchQuery={text.trim()}
          queryHash={queryHash}
          namespace={namespace}
          isOpen={isQueryBuilderDropdownOpen}
          hasFocus={shouldDisplayFocus}
          searchBarWrapperRef={searchBarWrapperRef}
          dropdownRef={queryBuilderDropdownRef}
          toggleDropdown={toggleQueryBuilderDropdown}
          onSearch={onQueryBuilderSearch}
          onMouseDown={onQueryBuilderButtonMouseDown}
        />
      )}
    </ServiceSubscription>
  );

  const renderRightContent = (isEmpty: boolean) => renderQueryBuilder();

  const maybeRenderSuggestionsDropdown = () => {
    if (!editorHasFocus || isQueryBuilderDropdownOpen) {
      return null;
    }

    return (
      <ServiceSubscription source={editor.services.textUnderCursor}>
        {({textUnderCursor}) => {
          if (textUnderCursor.anchorOffset !== textUnderCursor.focusOffset) {
            return null;
          }

          const query = textUnderCursor.text;
          const activeToken = findMemoizedActiveToken(query, textUnderCursor.focusOffset);

          return (
            <InboxesSearchSuggestionsDropdown
              anchor={searchBarWrapperRef}
              width={searchBarWrapperRef.current?.clientWidth}
              queryHash={queryHash}
              query={query}
              activeToken={activeToken}
              namespace={namespace}
              parameters={parameters}
              selectedSearchFilter={selectedSearchFilter}
              onSuggestionSelect={onSuggestionSelect}
            />
          );
        }}
      </ServiceSubscription>
    );
  };

  return (
    <>
      <SearchBar
        editor={editor}
        isDisabled={isDisabled}
        isFocused={shouldDisplayFocus}
        outerRef={searchBarWrapperRef}
        renderPlaceholder={renderPlaceholder}
        renderLeftContent={renderLeftContent}
        renderRightContent={renderRightContent}
        onKeyDown={onKeyDown}
        onBlur={onSearchBarBlur}
        onMouseDown={onEditorMouseDown}
        onClick={onBoxClick}
        onFocus={maybeUpdateSearchSessionTimestamp}
        onExitMouseDown={onExitMouseDown}
        onExitClick={onExitClick}
      />
      {maybeRenderSuggestionsDropdown()}
    </>
  );
};

/*
 * Helpers.
 */

const isRelatedTargetInsideRef = (event: FrontFocusEvent<HTMLDivElement>, ref: RefObject<HTMLDivElement>) =>
  Boolean(isNode(event.relatedTarget) && ref.current?.contains(event.relatedTarget));
